import React, { useState } from 'react'
import {
    Box,
    Button,
    Chip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'

import { axiosInstance } from '../../../../helpers'
import { Lead } from '../../../../models/Outreach/types'
import { formatApplicationFee } from '../../../../utils/applicationFee'
import {
    applicationFeeMark,
    getApplicationSubStatusDisplay,
} from '../../../../utils/applicationSubStatus'

const STATUS_CHIP_COLORS = {
    neutral: { bg: '#f3f4f6', fg: '#6b7280' },
    good: { bg: '#d1fae5', fg: '#047857' },
    warn: { bg: '#fef3c7', fg: '#b45309' },
    bad: { bg: '#ffe4e6', fg: '#be123c' },
} as const

function formatPaidDate(iso: string | null): string {
    if (!iso) return '—'
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    } catch {
        return '—'
    }
}

const useStyles = makeStyles((theme: Theme) => ({
    section: {
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
        paddingBottom: theme.spacing(1),
        borderBottom: `2px solid ${theme.palette.primary.main}`,
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(1.5),
        gap: theme.spacing(2),
    },
    fieldLabel: {
        minWidth: 160,
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        fontWeight: 600,
    },
    fieldValue: {
        flex: 1,
        fontSize: '0.875rem',
        wordBreak: 'break-word',
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
}))

import { AccountStatusChip } from '../../../LeasingSeason/LeadsPipeline/InviteToApplyDialog'
import { LeadWorkbookProgressPanel } from './LeadWorkbookProgressPanel'

interface LeadApplicationPanelProps {
    lead: Lead
    onInviteToApply?: (leadId: number) => Promise<unknown>
    onApproveApplication?: (leadId: number) => Promise<unknown>
    onSendScreeningLink?: (leadId: number) => Promise<unknown>
    isApproving?: boolean
    isSendingScreeningLink?: boolean
}

export const LeadApplicationPanel: React.FC<LeadApplicationPanelProps> = ({
    lead,
    onInviteToApply,
    onApproveApplication,
    onSendScreeningLink,
    isApproving = false,
    isSendingScreeningLink = false,
}) => {
    const classes = useStyles()
    const signing = lead.application_signing
    const feePayment = lead.application_fee_payment
    const statusDisplay = getApplicationSubStatusDisplay(
        lead.application_status,
    )
    const statusColors = STATUS_CHIP_COLORS[statusDisplay.tone]
    const canSendScreening =
        onSendScreeningLink &&
        (lead.pipeline_stage === 'app_signed_pending_review' ||
            signing?.pending_review ||
            (lead.pipeline_stage === 'app_sent' &&
                lead.application_status === 'submitted'))
    const canApprove =
        !onSendScreeningLink &&
        onApproveApplication &&
        (lead.pipeline_stage === 'app_signed_pending_review' ||
            signing?.pending_review)
    const canInvite = onInviteToApply && lead.account_status !== 'active'
    const [downloadingPdf, setDownloadingPdf] = useState(false)
    const [pdfError, setPdfError] = useState('')

    const snapshotEntries = Object.entries(signing?.submission_snapshot ?? {})
    const feeCents =
        lead.application_fee_cents ??
        signing?.application_fee_cents ??
        (feePayment?.amount != null
            ? Math.round(Number(feePayment.amount) * 100)
            : null)
    const feeMark = applicationFeeMark({
        feeCents,
        paidAt: feePayment?.paid_at,
        paymentStatus: feePayment?.status,
    })

    const handleDownloadSubmissionPdf = async () => {
        setDownloadingPdf(true)
        setPdfError('')
        try {
            const { data } = await axiosInstance.get<{ download_url: string }>(
                `outreach/leads/${lead.id}/application/submission-pdf/`,
            )
            if (data.download_url) {
                window.open(data.download_url, '_blank', 'noopener,noreferrer')
            } else {
                setPdfError('Submission PDF not available.')
            }
        } catch {
            setPdfError('Unable to download application PDF.')
        } finally {
            setDownloadingPdf(false)
        }
    }

    return (
        <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
                Application
            </Typography>

            <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>
                    Portal account
                </Typography>
                <Box style={{ flex: 1 }}>
                    <AccountStatusChip status={lead.account_status} />
                </Box>
            </Box>

            <Box className={classes.actions}>
                {canInvite && (
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => onInviteToApply(lead.id)}
                    >
                        {lead.account_status === 'invited'
                            ? 'Resend account invite'
                            : 'Invite to apply'}
                    </Button>
                )}
                {canApprove && (
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={isApproving}
                        onClick={() => onApproveApplication(lead.id)}
                    >
                        {isApproving
                            ? 'Approving…'
                            : 'Approve & move to Screening'}
                    </Button>
                )}
                {canSendScreening && (
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={isSendingScreeningLink}
                        onClick={() => onSendScreeningLink!(lead.id)}
                    >
                        {isSendingScreeningLink
                            ? 'Sending…'
                            : 'Send screening link'}
                    </Button>
                )}
                {signing?.status === 'COMPLETED' &&
                    signing.signed_pdf_download_url && (
                        <Button
                            variant="outlined"
                            color="primary"
                            href={signing.signed_pdf_download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Download signed PDF
                        </Button>
                    )}
                {lead.has_submission_pdf && (
                    <Button
                        variant="outlined"
                        color="primary"
                        disabled={downloadingPdf}
                        onClick={handleDownloadSubmissionPdf}
                    >
                        {downloadingPdf
                            ? 'Preparing…'
                            : 'Download application PDF'}
                    </Button>
                )}
            </Box>
            {pdfError && (
                <Typography
                    variant="body2"
                    color="error"
                    style={{ marginBottom: 12 }}
                >
                    {pdfError}
                </Typography>
            )}

            {(lead.application_status != null || feeCents != null) && (
                <>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.fieldLabel}>
                            Status
                        </Typography>
                        <Box style={{ flex: 1 }}>
                            <Chip
                                size="small"
                                label={statusDisplay.label}
                                style={{
                                    backgroundColor: statusColors.bg,
                                    color: statusColors.fg,
                                    fontWeight: 600,
                                }}
                            />
                            {feeMark != null && (
                                <Chip
                                    size="small"
                                    label={
                                        feeMark === 'paid'
                                            ? 'Fee paid'
                                            : 'Fee unpaid'
                                    }
                                    style={{
                                        marginLeft: 8,
                                        backgroundColor:
                                            feeMark === 'paid'
                                                ? '#d1fae5'
                                                : '#fef3c7',
                                        color:
                                            feeMark === 'paid'
                                                ? '#047857'
                                                : '#b45309',
                                        fontWeight: 600,
                                    }}
                                />
                            )}
                            {(signing?.pending_review ||
                                lead.pipeline_stage ===
                                    'app_signed_pending_review') && (
                                <Chip
                                    size="small"
                                    label="Pending review"
                                    style={{
                                        marginLeft: 8,
                                        backgroundColor: '#f59e0b',
                                        color: '#fff',
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                    {feeCents != null && (
                        <Box className={classes.fieldRow}>
                            <Typography className={classes.fieldLabel}>
                                Application fee
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {formatApplicationFee(feeCents)}
                            </Typography>
                        </Box>
                    )}
                    {feePayment?.paid_at && (
                        <Box className={classes.fieldRow}>
                            <Typography className={classes.fieldLabel}>
                                Fee paid
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                ${feePayment.amount} on{' '}
                                {formatPaidDate(feePayment.paid_at)}
                            </Typography>
                        </Box>
                    )}
                </>
            )}

            {signing ? (
                <>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.fieldLabel}>
                            Template
                        </Typography>
                        <Typography className={classes.fieldValue}>
                            {signing.signable_doc_name}
                        </Typography>
                    </Box>
                    {signing.signing_progress != null &&
                        signing.signing_progress > 0 && (
                            <Box className={classes.fieldRow}>
                                <Typography className={classes.fieldLabel}>
                                    Progress
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {signing.signing_progress}%
                                </Typography>
                            </Box>
                        )}

                    {signing.sync_conflicts.length > 0 && (
                        <Box mb={2}>
                            {signing.sync_conflicts.map((conflict) => (
                                <Alert
                                    key={`${conflict.field}-${conflict.status}`}
                                    severity="warning"
                                    style={{ marginBottom: 8 }}
                                >
                                    {conflict.field}:{' '}
                                    {conflict.reason ??
                                        (conflict.old
                                            ? `Updated from "${conflict.old}" to "${conflict.new}"`
                                            : `Set to "${conflict.new}"`)}
                                </Alert>
                            ))}
                        </Box>
                    )}

                    {snapshotEntries.length > 0 && (
                        <>
                            <Typography variant="subtitle2" gutterBottom>
                                Collected from signed application
                            </Typography>
                            {snapshotEntries.map(([field, value]) => (
                                <Box key={field} className={classes.fieldRow}>
                                    <Typography className={classes.fieldLabel}>
                                        {field}
                                    </Typography>
                                    <Typography className={classes.fieldValue}>
                                        {value}
                                    </Typography>
                                </Box>
                            ))}
                        </>
                    )}
                </>
            ) : (
                lead.application_status == null &&
                feeCents == null && (
                    <Typography variant="body2" color="textSecondary">
                        Invite the prospect to apply in the tenant portal.
                    </Typography>
                )
            )}

            <Box mt={3}>
                <Typography className={classes.sectionTitle}>
                    Application workbook
                </Typography>
                <LeadWorkbookProgressPanel leadId={lead.id} />
            </Box>
        </Box>
    )
}
